import { useEffect, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { ContactHeader } from '../components/ContactHeader.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { EntityChatPanel } from '../../ai-chat/components/ChatPanel.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { useDocuments } from '../../documents/hooks/useDocuments.js';
import { useContactInteractions } from '../hooks/useContactInteractions.js';
import { useContactFiles } from '../hooks/useContactFiles.js';
import { ContactFormModal } from '../components/ContactFormModal.js';
import { ContactInteractionsPanel } from '../components/ContactInteractionsPanel.js';
import { ContactSidebar } from '../components/ContactSidebar.js';
import { ContactOverview } from '../components/ContactOverview.js';
import { ContactDealsTab } from '../components/ContactDealsTab.js';
import { ContactTasksTab } from '../components/ContactTasksTab.js';
import { ContactMeetingsTab } from '../components/ContactMeetingsTab.js';
import { ContactNotesTab } from '../components/ContactNotesTab.js';
import { ContactDocumentsTab } from '../components/ContactDocumentsTab.js';
import { ContactFilesTab } from '../components/ContactFilesTab.js';
import { ContactAuditDrawer } from '../components/ContactAuditDrawer.js';
import {
  getContactById,
  getContactDetailQueryKey,
  type ContactInteractionItem,
} from '../api/contacts.client.js';

export function ContactDetailPage({ contactId }: { contactId: string }): ReactElement {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  useEffect(() => {
    setEditOpen(false);
    setHistoryOpen(false);
  }, [contactId]);
  const tasks = useTasks({ contactId });
  const notes = useNotes({ contactId });
  const meetings = useMeetings({ contactId });
  const documents = useDocuments({ contactId });
  const files = useContactFiles(contactId);
  const history = useContactInteractions(contactId);
  const query = useQuery({
    queryKey: getContactDetailQueryKey(workspaceId, contactId),
    queryFn: () => getContactById(contactId),
    enabled: scope !== null,
  });
  if (query.isLoading)
    return (
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: 24 }}>
        <Skeleton active />
      </main>
    );
  if (query.error || !query.data)
    return (
      <main style={{ padding: 24 }}>
        <ErrorState
          title={t('crm.entity_detail.contact_unavailable')}
          onRetry={() => void query.refetch()}
        />
      </main>
    );
  const contact = query.data;
  const interactions = history.data ?? contact.interactions;
  const historyItems: ContactInteractionItem[] = [
    ...interactions,
    ...(notes.data ?? []).map((note) => ({
      id: `note:${note.id}`,
      type: 'note',
      subject: null,
      body: note.body,
      channel: null,
      occurredAt: note.createdAt,
    })),
  ].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  return (
    <>
      <EntityDetailLayout
        header={
          <ContactHeader
            contact={contact}
            onChat={() => setChatOpen(true)}
            onEdit={() => setEditOpen(true)}
            onHistory={() => setHistoryOpen(true)}
          />
        }
        sidebar={<ContactSidebar contact={contact} interactions={historyItems} />}
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: (
                  <ContactOverview
                    description={contact.notes ?? null}
                    tasks={tasks.data ?? []}
                    meetings={meetings.data ?? []}
                    interactions={historyItems}
                  />
                ),
              },
              {
                key: 'deals',
                label: t('crm.entity_detail.deals'),
                count: contact.deals.length,
                content: <ContactDealsTab contactId={contactId} deals={contact.deals} />,
              },
              {
                key: 'tasks',
                label: t('crm.entity_detail.tasks'),
                count: tasks.data?.length ?? 0,
                content: <ContactTasksTab contactId={contactId} />,
              },
              {
                key: 'meetings',
                label: t('crm.entity_detail.meetings'),
                count: meetings.data?.length ?? 0,
                content: <ContactMeetingsTab contactId={contactId} />,
              },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: notes.data?.length ?? 0,
                content: <ContactNotesTab contactId={contactId} />,
              },
              {
                key: 'documents',
                label: t('crm.entity_detail.documents'),
                count: documents.documents.length,
                content: <ContactDocumentsTab contactId={contactId} />,
              },
              {
                key: 'files',
                label: t('crm.entity_detail.files'),
                count: files.data?.length ?? 0,
                content: <ContactFilesTab contactId={contactId} />,
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                count: historyItems.length,
                content: (
                  <ContactInteractionsPanel
                    compact
                    contactId={contactId}
                    notes={notes.data ?? []}
                  />
                ),
              },
            ]}
          />
        }
      />
      <Drawer
        data-testid="contact-chat-drawer"
        rootClassName="contact-chat-drawer"
        title={t('crm.chat.drawer_title')}
        placement="right"
        size="min(760px, 100vw)"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        styles={{ body: { padding: 0, overflow: 'hidden' } }}
      >
        <EntityChatPanel scope={scope} contextEntityType="contact" contextEntityId={contactId} />
      </Drawer>
      <ContactAuditDrawer
        contactId={contactId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      {editOpen ? (
        <ContactFormModal open contact={contact} onClose={() => setEditOpen(false)} />
      ) : null}
    </>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
